import React, { useState } from 'react';
import { post } from 'aws-amplify/api';
import { Modal, Form } from 'react-bootstrap';
import LoaderButton from '../reusable/LoaderButton';
import { useAppContext } from '../../App';

export default function UploadNewWordlistModal({ show, onHide, onSuccess }) {

  const { setUserPreference } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    author: '',
    link: '',
    description: '',
    file: null,
  });

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      setFormData((prev) => ({ ...prev, file: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Do mild validation of uploaded file
    const fileName = formData.file.name.toLowerCase();
    if (!fileName.endsWith('.txt') && !fileName.endsWith('.dict')) {
      alert('File type must be .txt or .dict!');
      return;
    }

    // Submit to server
    setIsLoading(true);
    try {
      // Get a pre-signed S3 URL from backend
      const apiResponse = await (
        await post({
          apiName: 'wordlists',
          path: '/baseWordlists',
          options: {
            body: {
              filename: formData.file.name,
              contentType: formData.file.type,

              name: formData.name,
              author: formData.author,
              description: formData.description,
              link: formData.link,
            },
          },
        }).response
      ).body.json();

      const { uploadUrl, baseWordlistId, wordlistId } = apiResponse;

      // Now upload the file to S3 directly
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': formData.file.type,
        },
        body: formData.file,
      });

      if (!uploadRes.ok) throw new Error('S3 upload failed');

      // Now, set it as the new active one and refresh
      await setUserPreference('activeWordlistId', wordlistId);

      if (onSuccess) onSuccess({ wordlistId, baseWordlistId });    // callback with the IDs of the new created wordlist
      onHide();
    } catch (err) {
      console.error('Upload failed', err);
      alert('Upload failed. Please get in touch if this continues.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} onShow={() => setFormData({
      name: '',
      author: '',
      description: '',
      file: null,
    })}>
      <Modal.Header className='fw-bold teal-3-bkgd' closeButton>
        Upload wordlist file
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>

          <Form.Group className="mb-3" controlId="formFile">
            <Form.Label className="mb-1">Upload File</Form.Label>
            <Form.Control
              size="sm"
              type="file"
              name="file"
              onChange={handleChange}
            />
          </Form.Group>

          <Form.Group className="mb-2" controlId="formName">
            <Form.Label className="mb-1">Name your new wordlist something memorable:</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter name"
            />
          </Form.Group>

          <Form.Group className="mb-2" controlId="formAuthor">
            <Form.Label className="mb-0 small">Author (optional)</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              name="author"
              value={formData.author}
              onChange={handleChange}
              placeholder="Enter author"
            />
          </Form.Group>

          <Form.Group className="mb-2" controlId="formLink">
            <Form.Label className="mb-0 small">Website (optional)</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              name="link"
              value={formData.link}
              onChange={handleChange}
              placeholder="Original reference URL for this wordlist"
            />
          </Form.Group>

          <Form.Group className="mb-2" controlId="formDescription">
            <Form.Label className="mb-0 small">Add a short description (optional)</Form.Label>
            <Form.Control
              size="sm"
              as="textarea"
              rows={2}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter description"
            />
          </Form.Group>

          <div className="d-flex justify-content-end">
            <LoaderButton isLoading={isLoading} disabled={!formData.file} variant="success" type="submit">
              Submit
            </LoaderButton>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

